import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import { epochLabel } from '@/features/projects/format'
import {
  fetchVersions,
  selectVersions,
  selectVersionsListErrorFor,
  selectVersionsLoadedKey,
  selectVersionsLoading,
  versionsRetryArmed
} from '@/features/versions/slices/versionsSlice'
import type { VersionRow } from '@/features/versions/types'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  intakesClosed,
  intakesRefetchArmed,
  loadSession,
  openIntakes,
  selectIntakes,
  selectIntakesBlocked,
  selectIntakesError,
  selectIntakesLoading,
  selectIntakesNonce,
  selectSelectedSession
} from '../slices/intakesSlice'
import type { IntakeAssigneeRow, IntakeLifecycleStatusValue, IntakeRow } from '../types'
import { SessionAnalysisPanel } from './SessionAnalysisPanel'
import { SessionTranscriptPanel } from './SessionTranscriptPanel'

/** Decision 4 (plan) — first ACTIVE, else first IN_PROGRESS, else the first row. */
function pickActiveVersion(versions: readonly VersionRow[]): VersionRow | undefined {
  return (
    versions.find((version) => version.status === 'ACTIVE') ??
    versions.find((version) => version.status === 'IN_PROGRESS') ??
    versions[0]
  )
}

const STATUS_TONE: Record<IntakeLifecycleStatusValue, StatusTone> = {
  draft: 'neutral',
  active: 'proposed',
  completed: 'done',
  'changes-requested': 'review',
  archived: 'neutral'
}

function assigneeLabel(assignee: IntakeAssigneeRow): string {
  const name = [assignee.firstName, assignee.lastName].filter(Boolean).join(' ').trim()
  return name !== '' ? name : (assignee.email ?? assignee.id)
}

/** One intake's assignee list — the sessions the consultant can select. */
function IntakeAssigneeList({
  intake,
  selected,
  onSessionSelected
}: {
  intake: IntakeRow
  selected: { intakeId: string; assigneeId: string } | null
  onSessionSelected: (intakeId: string, assigneeId: string) => void
}) {
  const { t } = useTranslation('projectWorkspace')
  const { assignees } = intake

  if (assignees.length === 0) {
    return <p className="mt-1.5 text-xs text-muted-foreground">{t('intakes.noAssignees')}</p>
  }

  return (
    <ul className="mt-1.5" data-testid={`intake-assignees-${intake.id}`}>
      {assignees.map((assignee) => {
        const isSelected = selected?.intakeId === intake.id && selected.assigneeId === assignee.id
        return (
          <li key={assignee.id}>
            <button
              type="button"
              onClick={() => onSessionSelected(intake.id, assignee.id)}
              aria-current={isSelected ? 'true' : undefined}
              className={
                'v-row flex w-full flex-wrap items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted' +
                (isSelected ? ' bg-muted font-medium' : '')
              }
            >
              <span className="text-foreground">{assigneeLabel(assignee)}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {assignee.role !== undefined && <span>{assignee.role}</span>}
                {assignee.state !== undefined && <span>{t(`intakes.assigneeState.${assignee.state}`)}</span>}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * The intakes list + session selection (PX-W7.1, AC-1/AC-2/AC-3/AC-4/AC-6/AC-7).
 *
 * Owns the active-version resolution (decision 4): reads `features/versions`
 * (no second fetch of that endpoint), picks ACTIVE > IN_PROGRESS > first row,
 * and renders the "no discovery cycle yet" empty state on zero versions
 * without issuing an intake request. Once a version is known it opens the
 * intake list and composes the session panels for whichever row is selected.
 */
export function IntakesPanel() {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const { projectId } = useParams()

  const versions = useAppSelector(selectVersions)
  const versionsLoading = useAppSelector(selectVersionsLoading)
  // Keyed to this project (TASK-63685491, F2b) — an error recorded for
  // another project never paints here, including on the first render after a
  // client-side switch, before this project's own `pending` action clears it.
  const versionsListError = useAppSelector((s) => selectVersionsListErrorFor(s, projectId))
  const versionsLoadedKey = useAppSelector(selectVersionsLoadedKey)

  const intakes = useAppSelector(selectIntakes)
  const intakesLoading = useAppSelector(selectIntakesLoading)
  const intakesError = useAppSelector(selectIntakesError)
  const blocked = useAppSelector(selectIntakesBlocked)
  const nonce = useAppSelector(selectIntakesNonce)
  const selected = useAppSelector(selectSelectedSession)

  // Fix cycle 1 (GRP-82297E9A): the versions slice keeps the PREVIOUS
  // project's rows while the new project's read is pending or after it
  // failed, so `versions` alone cannot say whose they are. Only rows loaded
  // for THIS project count — anything else runs the loading/blocked/error
  // ladder below instead of an empty intakes list for a stale version.
  const versionsCurrent = versionsLoadedKey === projectId
  const activeVersion = versionsCurrent ? pickActiveVersion(versions) : undefined

  // Fix cycle 2 (GRP-476B849C): true exactly when the CURRENT project's
  // versions are neither loaded, in flight, nor recorded as failed — the
  // same predicate the render ladder below already uses to decide between
  // `<LoadingPanel>` and the empty/error states (kept in sync with it
  // deliberately). Used ONLY as an extra effect dependency just below: a
  // DIFFERENT project's in-flight read settling (successfully) flips this
  // from `false` back to `true` for the project now on screen, waking the
  // fetch effect back up to actually ask for it.
  //
  // LOAD-BEARING — never replace with `versionsLoading` alone: `loading`
  // also flips true -> false on a FAILURE and `fetchFailed` never records
  // `loadedKey`, so that dep re-dispatches after every failure, forever (an
  // auto-retry loop). The `versionsListError === null` gate keeps a real
  // failure a single, non-retrying state; Retry stays the user's action.
  // `versionsListError` is keyed to this project (F2b) — an error recorded
  // for another project does not count, so a stale failure never blocks the
  // wake-up dep for the project actually on screen.
  const versionsStuck = !versionsCurrent && !versionsLoading && versionsListError === null

  // Reuse the versions slice already loaded elsewhere — no second fetch of an
  // endpoint another feature's slice already owns (interface contract §8.4).
  // U2-1 — dispatch unconditionally on `[orgId, projectId]` alone: the early
  // `versions.length > 0` return used to leave a PRIOR project's versions on
  // screen after a client-side project switch (a warm cache from project A
  // reads as "already loaded" for project B too). `fetchVersions`'s own
  // `shouldFetch` condition (keyed by `projectId`) is what actually dedupes.
  useEffect(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(fetchVersions({ orgId, projectId }))
  }, [dispatch, orgId, projectId, versionsStuck])

  // Fix cycle 3 (GRP-B04710BD, px-effect-cleanup-on-nonce-dep-resets-the-slice):
  // the reset is its OWN effect, keyed only on identity (`orgId`/`projectId`),
  // so its cleanup fires on unmount and on a project change — never on a
  // realtime nonce bump. Folding it into the fetch effect below used to mean
  // React ran this cleanup (which wipes the selected session, its transcript
  // and analysis, along with everything else) on EVERY dependency change, not
  // only at unmount — a remote realtime frame silently closed the open
  // session.
  useEffect(() => {
    return () => {
      dispatch(intakesClosed())
    }
  }, [dispatch, orgId, projectId])

  // The mount/refetch effect — no cleanup. A version resolution or a nonce
  // bump only needs fresh data; `openIntakes`'s own `shouldFetch` condition
  // (keyed by `projectId:versionId`, invalidated by the same realtime action
  // that bumps `nonce`) turns either into exactly one request.
  // U2-1 — additionally gated on `versionsLoadedKey === projectId`: without
  // it, a project switch can pair the NEW projectId with the OLD project's
  // still-resolved `activeVersion` for one render before the versions effect
  // above's fetch resolves, issuing an `openIntakes` request for a
  // project/version pair that never coexisted server-side.
  useEffect(() => {
    if (orgId === null || projectId === undefined || activeVersion === undefined) return
    if (versionsLoadedKey !== projectId) return
    void dispatch(openIntakes({ orgId, projectId, versionId: activeVersion.id }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orgId, projectId, activeVersion?.id, versionsLoadedKey, nonce])

  if (orgId === null || projectId === undefined) return null

  const onRetryVersionsClicked = (): void => {
    dispatch(versionsRetryArmed())
    void dispatch(fetchVersions({ orgId, projectId }))
  }
  const onRetryIntakesClicked = (): void => {
    if (activeVersion === undefined) return
    dispatch(intakesRefetchArmed())
    void dispatch(openIntakes({ orgId, projectId, versionId: activeVersion.id }))
  }
  const onSessionSelected = (intakeId: string, assigneeId: string): void => {
    if (activeVersion === undefined) return
    void dispatch(loadSession({ orgId, projectId, versionId: activeVersion.id, intakeId, assigneeId }))
  }

  if (versions.length === 0 || !versionsCurrent) {
    // Not yet loaded for this project and no failure recorded ⇒ still loading
    // (covers the first render after a switch, before the pending action).
    if (versionsLoading || (!versionsCurrent && versionsListError === null)) {
      return <LoadingPanel label={t('loading')} />
    }
    // c1-2 — `features/versions` now carries the real HTTP status on its
    // list failure (`VersionsError.status`), so a genuine fetch failure and a
    // genuinely empty version list are honestly distinguishable: a 403/404
    // routes to the SAME blocked card the page-level gate uses, any other
    // failure to the shared error panel with a retry, and only a clean empty
    // read falls through to the empty state below.
    if (versionsListError !== null && (versionsListError.status === 403 || versionsListError.status === 404)) {
      return (
        <ProjectBlockedState
          blocked={{
            kind: 'blocked',
            status: versionsListError.status,
            code: versionsListError.key,
            message: versionsListError.message
          }}
        />
      )
    }
    if (versionsListError !== null) {
      return (
        <ErrorPanel
          error={{
            status: versionsListError.status,
            backendKey: versionsListError.key,
            message: versionsListError.message
          }}
          onRetryClicked={onRetryVersionsClicked}
        />
      )
    }
    return <EmptyPanel title={t('intakes.noVersionTitle')} body={t('intakes.noVersionBody')} />
  }

  if (blocked !== null) {
    return (
      <ProjectBlockedState
        blocked={{ kind: 'blocked', status: blocked.status, code: blocked.code, message: blocked.message }}
      />
    )
  }

  return (
    <section aria-label={t('intakes.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <SectionLabel>{t('intakes.panelTitle')}</SectionLabel>

      {intakesLoading ? (
        <div className="mt-2">
          <LoadingPanel label={t('loading')} />
        </div>
      ) : intakesError !== null ? (
        <div className="mt-2">
          <ErrorPanel
            error={{ status: intakesError.status, backendKey: intakesError.code, message: intakesError.message }}
            onRetryClicked={onRetryIntakesClicked}
          />
        </div>
      ) : intakes.length === 0 ? (
        <div className="mt-2">
          <EmptyPanel title={t('intakes.emptyTitle')} body={t('intakes.emptyBody')} />
        </div>
      ) : (
        <ul className="mt-2 space-y-3" data-testid="intakes-list">
          {intakes.map((intake) => (
            <li key={intake.id} className="rounded-md border border-border px-3 py-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-serif text-sm font-semibold text-foreground">{intake.title}</span>
                <span className="flex items-center gap-2">
                  <StatusPill tone={STATUS_TONE[intake.status]}>{t(`intakes.status.${intake.status}`)}</StatusPill>
                  <span className="v-figure text-xs tabular-nums text-muted-foreground">
                    {t('intakes.assigneeCount', { count: intake.assignees.length })}
                  </span>
                </span>
              </div>
              {intake.createdAt !== undefined && (
                <p className="mt-0.5 text-xs text-muted-foreground">{epochLabel(intake.createdAt)}</p>
              )}
              <IntakeAssigneeList intake={intake} selected={selected} onSessionSelected={onSessionSelected} />
            </li>
          ))}
        </ul>
      )}

      {selected !== null && (
        <div className="mt-4 space-y-3">
          <SessionTranscriptPanel />
          <SessionAnalysisPanel />
        </div>
      )}
    </section>
  )
}
